"use client"

import { useQuery } from "@tanstack/react-query"
import { clsx } from "clsx"

import type { GID } from "@unlikelystudio/commerce-connector"

import type { PropsWithClassName } from "~/@types/generics"
import { useLocale } from "~/lib/i18n/hooks/useLocale"
import { VARIANT_FIRST } from "~/lib/shopify/constants/variant"
import { getSfProduct } from "~/lib/shopify/queries/get-sf-product"
import { type TVariant } from "~/lib/shopify/serializers/serialize-variants"
import { Image } from "~/components/ui/Image"
import type { TProductHeader } from "~/components/ui/Product/ProductHeader/_data/serializer"
import RichText from "~/components/ui/RichText"

import * as css from "./styles.css"

export type ProductFomoProps = PropsWithClassName<
  {
    productId: GID
    currentVariant: TVariant
  } & NonNullable<TProductHeader["globalProduct"]>["fomo"]
>

function ProductFomo({ className, productId, currentVariant, limit, logo, message }: ProductFomoProps) {
  const locale = useLocale()

  const { data: quantity } = useQuery({
    queryKey: ["product-fomo", currentVariant?.id],
    queryFn: async () => {
      const { product } = await getSfProduct({
        id: productId,
        includeQuantityAvailable: true,
        variantsFirst: VARIANT_FIRST,
        locale,
      })

      return product?.variants.find((variant) => variant.id === currentVariant.id)?.quantityAvailable ?? null
    },
    keepPreviousData: true,
    initialData: currentVariant?.quantityAvailable,
    // refetch every 5 minutes
    refetchInterval: 1000 * 60 * 10,
  })

  return quantity && limit && quantity > 0 && quantity <= limit ? (
    <div className={clsx(className)}>
      <p className={css.message}>
        {logo && <Image className={css.logo} {...logo} />}
        <RichText className={css.message} render={message} />
      </p>
    </div>
  ) : null
}

export default ProductFomo
